import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
import './i18n';
import { useUIStore } from './store/uiStore';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// Initialize theme before render to prevent flash
const initTheme = () => {
  const stored = localStorage.getItem('pv-admin-ui');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      const theme = state?.theme || 'dark';
      document.documentElement.classList.toggle('dark', theme === 'dark');
      document.documentElement.classList.toggle('light', theme === 'light');
    } catch {
      document.documentElement.classList.add('dark');
    }
  } else {
    document.documentElement.classList.add('dark');
  }
};

initTheme();

// The app shell is cached for the home-screen app (public/sw.js). Production only, so a dev server is never cached.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* the admin works without it */ });
  });
}

// App wrapper to initialize store
export const AppWrapper = () => {
  React.useEffect(() => {
    useUIStore.getState().initializeTheme();
  }, []);

  return <>
    {/* Bottom left, so a toast never sits over the action buttons at the top of a page. */}
    <ToastContainer
        position="bottom-left"
        autoClose={3000}
        theme="colored"
      />
    <App />
  </>;
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}>
        <AppWrapper />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
